'use client';

import { IconSignal } from '../../../server/icon/IconSignal';
import type { ProductLabel } from '../../../../product/productLabel';

import type { DiscountBanner, StockInfo } from '../../../../product/index';
import {
  AccountHeroDecoration,
  AccountHeroDecorationVariant,
} from '@vs-shop/commerce-ui/accountHero';
import {
  ProductBundle,
  ProductBundleJoin,
  ProductBundleJoinVariant,
  ProductBundleLine,
  ProductBundleList,
  ProductBundleSummary,
  ProductBundleTitle,
} from '@vs-shop/commerce-ui/productBundle';
import { Heading } from '@vs-shop/ui/heading';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import type { ProductAccessoryFields } from '../../../../product/productAccessoryFields';
import { mapProductToAccessoryItem } from '../../../../utils/mapAccessoryItemsToProductCards';

import ProductDiscountSetItem from './ProductDiscountSetItem';
import ProductDiscountSetTotal from './ProductDiscountSetTotal';

import type { ProductImage } from '../../../../effect/schema/openapi/backend';

/** Only the fields `mapProductToAccessoryItem` reads off the main product. */
type HasMainProductFields = {
  readonly name: string;
  readonly metaTitle: string | null;
  readonly code: string;
  readonly routerUrl: string;
  readonly shortDescription: string;
  readonly subtitle: string | null;
  readonly price: number;
  readonly priceWithoutTax: number;
  readonly originalPrice: number | null;
  readonly discountBanner: DiscountBanner | null;
  readonly labels: readonly ProductLabel[];
  readonly ageRestriction: 'none' | '18+';
};

type Props = {
  sets: readonly ProductAccessoryFields[];
  mainProduct: HasMainProductFields;
  mainProductImage: ProductImage | null;
  stock: StockInfo;
};

/**
 * rcprofi's "often bought together": the shared `@vs-shop/commerce-ui` bundle
 * parts around this tenant's items, prices and cart.
 *
 * Which lines are in the offer is state this component holds; the brand mark
 * behind the block is this tenant's own decoration and sits outside the
 * section, because it belongs to the page's ground rather than to the offer.
 */
export default function ProductDiscountSet({ sets, mainProduct, mainProductImage, stock }: Props) {
  const intl = useIntl();

  if (sets.length === 0) {
    return null;
  }

  const mainProductItem = mapProductToAccessoryItem(mainProduct, mainProductImage, stock);

  const allItems = [mainProductItem, ...sets];

  const [selected, setSelected] = useState<string[]>(allItems.map((i) => i.code));

  const handleToggle = (id: string) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const selectedItems = allItems.filter((i) => selected.includes(i.code));
  const totalOriginal = selectedItems.reduce((sum, item) => sum + item.price, 0);

  return (
    <>
      <ProductBundle>
        <ProductBundleTitle render={<Heading level={2} />}>
          {intl.formatMessage({
            id: 'component.client.page.product.productDiscountSet.title',
            defaultMessage: 'Často nakupované společně',
          })}
        </ProductBundleTitle>

        <ProductBundleList>
          {allItems.map((item, index) => {
            const showPlusSign = index < allItems.length - 1;

            return (
              <ProductBundleLine key={index}>
                <ProductDiscountSetItem
                  imagePath={item.imagePath}
                  routerUrl={item.routerUrl}
                  productName={item.name}
                  label={item.label}
                  price={item.price}
                  isSelected={selected.includes(item.code)}
                  onToggleAction={() => {
                    handleToggle(item.code);
                  }}
                />

                <ProductBundleJoin
                  variant={
                    showPlusSign ? ProductBundleJoinVariant.STATED : ProductBundleJoinVariant.BLANK
                  }
                />
              </ProductBundleLine>
            );
          })}

          <ProductBundleSummary>
            <ProductDiscountSetTotal
              selectedCount={selectedItems.length}
              totalOriginal={totalOriginal}
              selectedItems={selectedItems}
            />
          </ProductBundleSummary>
        </ProductBundleList>
      </ProductBundle>

      {/* SIGNAL EFFECT */}
      <AccountHeroDecoration variant={AccountHeroDecorationVariant.BLEEDING_UPPER_LEFT_GROWING}>
        <IconSignal className="text-primary/10" />
      </AccountHeroDecoration>
    </>
  );
}
